/* eslint-disable react-hooks/exhaustive-deps */
import Cookies from "js-cookie";
import { Suspense, useCallback, useContext, useEffect, useRef } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { getUserDetailsApi } from "../api/user/getUserDetails";
import { ROUTES } from "../constant";
import { ENVIRONMENT_TYPE } from "../constant/common";
import { WEB_URL, environment } from "../constant/environment";
import { MCP_CONTINUE_PATH } from "../constant/mcpWidget";
import { StateContext } from "../context/globalContext/context";
import { iGlobalContext } from "../context/globalContext/interface";
import { setItemInLocalStorage } from "../hooks/localStorage";
import { isValidJWT } from "../utils/authentication/isValidJWT";
import { isAllowedPartnerOrigin, isEmbedMode } from "../utils/embedMode";
import { rememberPortalOrigin, PORTAL_INBOUND, PORTAL_MESSAGES, postToPortal } from "../utils/embedPortalBridge";
import { getActingPartner, getActingPartnerCountry, setActingPartner } from "../utils/actingPartner";
import { getEmbedHost, isPartnerHost, setEmbedHost } from "../utils/embedHost";
import { adoptPartnerBranding, applyBrandingToDocument, resolvePartnerBranding, setBrandingPreview } from "../utils/partnerBranding";
import axios from "axios";
import { embedAuth, embedAuthWarn, describeJwt, readEmbedClaims } from "../utils/embedAuthLog";
import { API_KEY, ENDPOINT } from "../constant/environment";
import { PARTNER_ORIGIN_ALLOWLIST } from "../utils/embedMode";
import TripHelp from "./tripHelp/TripHelp";
// OnBoarding is the landing route for most unauthenticated users; keeping it
// eager avoids a lazy-chunk round-trip on the LCP critical path.
import OnBoarding from "./onBoarding/onBoarding";
import LoadingDots, { SHOW_LOADING_DOTS }  from "../component/reusableComponent/loadingDots/LoadingDots";
import MobileScrollContainer from "../component/reusableComponent/mobileScrollContainer/MobileScrollContainer";
import BookingFormSkeleton from "../component/BookingInProgress/component/bookingFormSkeleton/BookingFormSkeleton";
import DelayedFallback from "../component/reusableComponent/delayedFallback/DelayedFallback";
import ChatHomeSkeleton from "../component/TripManagement/newTripPage/components/ChatHomeSkeleton";
import MobileAccount from "./account/MobileAccount";
// The post-payment landing pages are bundled eagerly, never code-split: Stripe
// has already charged the card when the app navigates here, and the page that
// sends the booking confirmation must not depend on fetching a chunk a deploy
// may have deleted (Oct 2026: a stale tab 404'd this chunk → white page →
// paid, never confirmed).
import StripeSuccess from "./payment-success";
import PaymentFailure from "./payment-failure";
import lazyWithRetry from "../utils/lazyWithRetry";

// Code splitting: Lazy load route components using dynamic import()
const NotFound = lazyWithRetry(() => import("../component/NotFound/NotFound"));
const AboutUs = lazyWithRetry(() => import("../component/aboutUs/aboutUs"));
const DesktopLandingPage = lazyWithRetry(() => import("../component/home&Landingpage/desktopLandingPage/DesktopLandingPage"));
const ExploreTripAndAssistance = lazyWithRetry(() => import("./exploreTripAndAssistance/ExploreTripAndAssistance"));
const FaqAssistance = lazyWithRetry(() => import("./faqAssistance/FaqAssistance"));
const HotelDetails = lazyWithRetry(() => import("../component/reusableComponent/hotelCard/components/hotelDetails"));
const MyTrips = lazyWithRetry(() => import("./myTripsPage/myTrips"));
const RecommendedTripDetailsPage = lazyWithRetry(
  () => import("../component/recommendedTripDetailsPage/RecommendedTripDetailsPage")
);
const TripDetailsPage = lazyWithRetry(() => import("./tripDetailsPage/TripDetailsPage"));
const EmailVerificationMessage = lazyWithRetry(() => import("../component/verifyEmail/EmailVerificationMessage"));
const TodayTrip = lazyWithRetry(() => import("../routes/todayTrip/todayTrip"));
const TravelerInformation = lazyWithRetry(() => import("../routes/travelerInformation/travellerInformation"));
const AdjustTrip = lazyWithRetry(() => import("./adjustTrip/adjustTrip"));
const BookingInProgress = lazyWithRetry(() => import("./bookingInProgress/BookingInProgress"));
const BookingLoader = lazyWithRetry(() => import("../component/BookingInProgress/component/bookingLoader/BookingLoader"));
const EditAccount = lazyWithRetry(() => import("./editAccount/EditAccount"));
const FaqAccordion = lazyWithRetry(() => import("./faqsAccordion/faqsAccordion"));
const FlightDelay = lazyWithRetry(() => import("./flightDelay/flightDelay"));
const FlightSelect = lazyWithRetry(() => import("./flightSelect/flightSelect"));
const ItineraryDetails = lazyWithRetry(() => import("./itineraryDetails/ItineraryDetails"));
const ModifyFlights = lazyWithRetry(() => import("./modifyFlights/modifyFlights"));
const ModifyHotelReservation = lazyWithRetry(() => import("./modifyHotelReservation/modifyHotelReservation"));
const ModifyHotelsSuccess = lazyWithRetry(() => import("./modifyHotelsSuccess/modifyHotelsSuccess"));
const NewTrip = lazyWithRetry(() => import("./newTrip/newTrip"));
const PreviewPreference = lazyWithRetry(() => import("./previewPreference/previewPreference"));
const PrivacyPolicy = lazyWithRetry(() => import("./privacy-policy/privacy-policy"));
const ProtectedRoute = lazyWithRetry(() => import("./protectedRoute/ProtectedRoute"));
const RecommendedTrips = lazyWithRetry(() => import("./recommendedTrips/RecommendedTrips"));
const ReorderMultiCityTrip = lazyWithRetry(() => import("./reorderMultipCityTrip/ReorderMultiCityTrip"));
const ResetPasswordPage = lazyWithRetry(() => import("./resetPassword/ResetPasswordPage"));
const SavedItineraryDetail = lazyWithRetry(() => import("./savedItineraryDetails/SavedItineraryDetail"));
const TermsOfService = lazyWithRetry(() => import("./termsOfService/termsOfService"));
const Test = lazyWithRetry(() => import("./testPage/Test"));
const TopDestination = lazyWithRetry(() => import("./topDestination/TopDestination"));
const ViewShareItinerary = lazyWithRetry(() => import("./viewSharedItineraryDetails/ViewShareItinerary"));
const TripsById = lazyWithRetry(() => import("./tripsByItinerary/TripsById"));
const WeatherAdvisory = lazyWithRetry(() => import("./weatherAdvisory/weatherAdvisory"));
const TripDetailsRedirect = lazyWithRetry(() => import("./redirectRoutes/TripDetailsRedirect"));
const VerifyEmailRedirect = lazyWithRetry(() => import("./redirectRoutes/VerifyEmailRedirect"));
const SavedTrip = lazyWithRetry(() => import("./savedTrip/SavedTrip"));
const TicketDetails = lazyWithRetry(() => import("./ticketDetails/TicketDetails"));
const HistoryPage = lazyWithRetry(() => import("./historyPage/HistoryPage"));
const Preferences = lazyWithRetry(() => import("./preferences/preferences"));
const GoogleOAuth = lazyWithRetry(() => import("./googleOAuth/GoogleOAuth"));
const PortalSurface = lazyWithRetry(() => import("./portal/PortalSurface"));
const McpContinue = lazyWithRetry(() => import("./mcpContinue/McpContinue"));

export default function AppRoutes() {
  // Master switch for the Suspense fallback indicator. Flip to `false` to disable the centered "Loading" + wavy dots entirely — the component renders nothing and the Suspense fallback effectively becomes empty (purple body shows through during lazy chunk loads, the pre-fix behavior). Co-located with the component so toggling is one edit.
// const SHOW_LOADING_DOTS = true;
  const context = useContext(StateContext);
  const { userInfo, setUserInfo, accessJWT, isMobile, setAccessJWT, branding, setBranding }: iGlobalContext = context;

  // The identity the most recent getUserDetails() call was issued for. A
  // response for any other id is stale (superseded or signed out) and must
  // not land: applied last, it silently swapped the frame to a different
  // person while the token and acting partner stayed the operator's.
  const identityRequestRef = useRef<string | null>(null);

  async function getUserDetails(userId: string, tokenOverride?: string) {
    identityRequestRef.current = userId;
    const token = tokenOverride || accessJWT;
    embedAuth("identity.resolve.request", {
      userId,
      usedPortalToken: !!tokenOverride,
      accessJwt: describeJwt(token),
    });
    const response = await getUserDetailsApi(token, userId);
    if (identityRequestRef.current !== userId) {
      embedAuthWarn("identity.resolve.stale", {
        requested: userId,
        current: identityRequestRef.current,
        responseId: response?.id ?? null,
      });
      return;
    }
    // A backend error envelope comes back as `data: []` (truthy, no id) — treat
    // anything that isn't the requested user as no answer, never as a login.
    if (!response || String(response.id) !== String(userId)) {
      embedAuthWarn("identity.resolve.empty", { userId, responseId: response?.id ?? null });
      return;
    }
    embedAuth("identity.resolved", {
      id: response.id,
      email: response.email,
      name: response.name,
      role: response?.role || 0,
    });
    setUserInfo((prevInfo) =>
      prevInfo?.id !== userId
        ? prevInfo
        : {
            ...prevInfo,
            id: response.id,
            name: response.name,
            email: response.email,
            phone: response.phone,
            profile_pic: response.profile_pic,
            loggedIn: true,
            role: response?.role || 0,
          }
    );
  }

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLocalSession = useCallback(() => {
    identityRequestRef.current = null;
    setUserInfo({
      id: "",
      name: "",
      email: "",
      phone: "",
      profile_pic: "",
      loggedIn: false,
      role: 0,
    });
    window.localStorage.removeItem("_zenvoya_JWT");
    if (isEmbedMode()) setAccessJWT("");
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();

  const PORTAL_SURFACE_ROUTES: Record<string, string> = {
    chat: ROUTES.PortalChat,
    shop: ROUTES.PortalShop,
    history: ROUTES.PortalHistory,
    trips: ROUTES.PortalTrips,
  };

  // Last host|partner|country logged, so the portal's re-posts (token rotation,
  // session-recover) don't repeat the line — only an open or a partner switch logs.
  const portalCountryLogKeyRef = useRef<string | null>(null);

  const logEmbedPortalCountry = () => {
    const host = getEmbedHost();
    if (!host) return;
    const partner = getActingPartner();
    const country = getActingPartnerCountry() || "unknown";
    const key = `${host}|${partner?.partnerCode ?? ""}|${country}`;
    if (portalCountryLogKeyRef.current === key) return;
    portalCountryLogKeyRef.current = key;
    // eslint-disable-next-line no-console
    console.log(
      `[embed-portal] opened in ${host.toUpperCase()} portal — country: ${country}` +
        (partner ? ` (partner: ${partner.name || "—"}, ${partner.partnerCode})` : ""),
    );
  };

  const onSsoMessageRef = useRef<(event: MessageEvent) => void>();

  onSsoMessageRef.current = (event: MessageEvent) => {
    // Same-origin marketing iframe (`zenvoya.ai` → `chat.zenvoya.ai`) is the
    // primary SSO sender. In third-party embed mode, additionally accept
    const sameOrigin = `${event.origin}/` === WEB_URL;
    const partnerOriginOk = isEmbedMode() && isAllowedPartnerOrigin(event.origin);
    if (!sameOrigin && !partnerOriginOk) {
      let looksLikeSso = false;
      try {
        const probe = JSON.parse(event.data);
        looksLikeSso =
          !!probe && typeof probe === "object" && ("loggedIn" in probe || "jwt" in probe);
      } catch {
        looksLikeSso = false;
      }
      if (!looksLikeSso) return;
      embedAuthWarn(
        "sso.rejected.origin",
        {
          origin: event.origin,
          sameOrigin,
          embedMode: isEmbedMode(),
          originAllowlisted: isAllowedPartnerOrigin(event.origin),
          allowlist: PARTNER_ORIGIN_ALLOWLIST,
          hint: !isEmbedMode()
            ? "embed mode not detected — is the iframe URL missing ?embed=1 ?"
            : "origin not in REACT_APP_PORTAL_ORIGINS",
        },
        true
      );
      return;
    }
    if (partnerOriginOk) rememberPortalOrigin(event.origin);
    let data: any;
    try {
      data = JSON.parse(event.data);
    } catch {
      embedAuthWarn("sso.rejected.payload", {
        origin: event.origin,
        reason: "not JSON",
        typeofData: typeof event.data,
      });
      return;
    }
    if (data?.type === PORTAL_INBOUND.setSurface) {
      const target = PORTAL_SURFACE_ROUTES[data?.surface];
      if (!partnerOriginOk || !target) {
        embedAuthWarn("surface.switch.rejected", {
          origin: event.origin,
          surface: data?.surface,
          partnerOriginOk,
          reason: target ? "origin not an allowlisted portal" : "unknown surface",
        });
        return;
      }
      embedAuth("surface.switch", { origin: event.origin, surface: data.surface, target });
      navigate(target, { replace: true });
      return;
    }

    // Theme Studio preview — only from an allowlisted portal, never the
    // same-origin marketing frame. Nothing is cached or saved: it lives until the
    // portal clears it (`branding: null`) or the frame reloads.
    if (data?.type === PORTAL_INBOUND.themePreview) {
      if (!partnerOriginOk) return;
      const preview = setBrandingPreview(data?.branding ?? null);
      if (preview) setBranding(preview);
      applyBrandingToDocument(preview ?? branding);
      return;
    }

    if (typeof data?.id !== "undefined" && data?.loggedIn) {
      setActingPartner(data?.actingPartner);
      setEmbedHost(data?.host);
      logEmbedPortalCountry();
      resolvePartnerBranding(isPartnerHost() ? getActingPartner()?.partnerCode : null, data?.jwt)
        .then(setBranding)
        .catch(() => {});

      if (userInfo?.loggedIn && userInfo?.id === data.id) {
        const rotated = isEmbedMode() && typeof data?.jwt === "string" ? data.jwt : undefined;
        if (rotated && rotated !== accessJWT) {
          setAccessJWT(rotated);
          scheduleEmbedRefresh(rotated);
          setItemInLocalStorage("_zenvoya_JWT", rotated);
          embedAuth("sso.token.rotated", {
            origin: event.origin,
            id: data.id,
            jwt: describeJwt(rotated),
          });
        } else {
          embedAuth("sso.repeat.ignored", { origin: event.origin, id: data.id });
        }
        return;
      }
      embedAuth("sso.accepted", {
        origin: event.origin,
        via: sameOrigin ? "same-origin" : "partner-allowlist",
        id: data.id,
        email: data?.email ?? null,
        name: data?.name ?? null,
        jwt: describeJwt(data?.jwt),
        actingPartner: getActingPartner(),
      });
      setUserInfo(data);
      const portalToken = isEmbedMode() && typeof data?.jwt === "string" ? data.jwt : undefined;
      if (portalToken) {
        setAccessJWT(portalToken);
        scheduleEmbedRefresh(portalToken);
      }
      getUserDetails(data.id, portalToken);
      // store the JWT to local storagex
      setItemInLocalStorage("_zenvoya_JWT", data.jwt);
      return;
    }
    if (!userInfo?.loggedIn && !userInfo?.id) return;
    embedAuth("sso.signout", { origin: event.origin, previousId: userInfo?.id });
    setActingPartner(null);
    setEmbedHost(null);
    portalCountryLogKeyRef.current = null;
    resolvePartnerBranding(null)
      .then(setBranding)
      .catch(() => {});
    clearLocalSession();
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event: MessageEvent) => onSsoMessageRef.current?.(event);
    window.addEventListener("message", handler);

    if (isEmbedMode()) {
      const delivered = postToPortal({ type: PORTAL_MESSAGES.sessionRecover });
      embedAuth("sso.requested", {
        delivered,
        note: "listener is live; asked the portal to post the identity",
      });
    }

    return () => window.removeEventListener("message", handler);
  }, []);

  const scheduleEmbedRefresh = useCallback((token: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (!isEmbedMode() || !token) return;

    const claims = readEmbedClaims(token);
    if (!claims || claims.scope !== "embed" || !claims.exp) return;

    const leadMs = 60 * 1000;
    const delay = claims.exp * 1000 - Date.now() - leadMs;
    if (delay <= 0) {
      embedAuthWarn("refresh.skipped", { reason: "token already at or past expiry" });
      return;
    }

    refreshTimerRef.current = setTimeout(async () => {
      embedAuth("refresh.request", { expiresAt: new Date(claims.exp * 1000).toISOString() });
      try {
        const response = await axios.post(
          `${ENDPOINT}/app/v1/embed/refresh`,
          { accessToken: token },
          { headers: { "Content-Type": "application/json", "x-api-key": API_KEY || "" } },
        );
        const next = response?.data?.data?.jwt;
        if (!next) {
          embedAuthWarn("refresh.empty", { message: response?.data?.message ?? null });
          return;
        }
        embedAuth("refresh.accepted", { jwt: describeJwt(next) });
        setAccessJWT(next);
        setItemInLocalStorage("_zenvoya_JWT", next);
        scheduleEmbedRefresh(next);
      } catch (e: any) {
        embedAuthWarn("refresh.failed", {
          status: e?.response?.status ?? null,
          message: e?.response?.data?.error?.message ?? e?.message ?? null,
        });
      }
    }, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isEmbedMode()) return;

    const params = new URLSearchParams(window.location.search);
    const ticket = params.get("ticket");
    if (!ticket) return;

    params.delete("ticket");
    const cleaned = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
    window.history.replaceState({}, "", cleaned);

    if (userInfo?.loggedIn || userInfo?.id) {
      embedAuth("ticket.session.cleared", {
        reason: "ticket presented — previous session is stale until redeem succeeds",
        previousId: userInfo?.id ?? null,
      });
      clearLocalSession();
    }

    let cancelled = false;
    (async () => {
      embedAuth("ticket.redeem.request", { ticketLength: ticket.length });
      try {
        const response = await axios.post(
          `${ENDPOINT}/app/v1/embed/redeem`,
          { ticket },
          { headers: { "Content-Type": "application/json", "x-api-key": API_KEY || "" } },
        );
        const data = response?.data?.data;
        if (cancelled) return;
        if (!data?.userId || !data?.jwt) {
          embedAuthWarn("ticket.redeem.empty", {
            message: response?.data?.message ?? null,
            note: "frame stays signed out — the previous session was cleared before this call",
          });
          return;
        }
        embedAuth("ticket.redeem.accepted", { id: data.userId, jwt: describeJwt(data.jwt) });
        if (data.partner?.partnerCode) {
          setEmbedHost("partner");
          setActingPartner({ ...data.partner, source: "ticket" });
          embedAuth("ticket.partner.adopted", {
            partnerCode: data.partner.partnerCode,
            branchCode: data.partner.branchCode ?? null,
            channel: data.partner.channel ?? null,
            currency: data.partner.currency ?? null,
            verticals: data.partner.verticals ?? null,
          });
          setBranding(adoptPartnerBranding(data.partner.branding, data.partner.partnerCode));
        }
        setUserInfo({
          id: data.userId,
          name: "",
          email: "",
          phone: "",
          profile_pic: "",
          loggedIn: true,
          role: 0,
        });
        setAccessJWT(data.jwt);
        setItemInLocalStorage("_zenvoya_JWT", data.jwt);
        getUserDetails(data.userId, data.jwt);
        scheduleEmbedRefresh(data.jwt);
      } catch (e: any) {
        if (!cancelled) {
          embedAuthWarn("ticket.redeem.failed", {
            status: e?.response?.status ?? null,
            message: e?.response?.data?.error?.message ?? e?.message ?? null,
            note: "frame stays signed out — the previous session was cleared before this call",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (environment === ENVIRONMENT_TYPE.LOCAL) return;
    const userData = Cookies.get(`${environment}userData`);
    // The `.zenvoya.ai` cookie is written by a DIRECT login and is visible to
    // every same-site frame, so inside an operator portal it names whoever last
    // signed in to the app directly — not the person being booked for. Adopting
    // it here raced the portal's SSO identity and, when its lookup resolved
    // last, replaced the acting traveller with that unrelated account. Neither
    // read nor wipe it in embed: the portal posts the identity.
    if (isEmbedMode()) {
      embedAuth("cookie.skipped", {
        reason: "embed frame — identity comes from the portal, never the shared cookie",
        cookiePresent: !!userData,
      });
      return;
    }
    if (userData) {
      const data = JSON.parse(userData);
      if (typeof data?.id !== "undefined" && data?.loggedIn && isValidJWT(data.jwt)) {
        embedAuth("cookie.accepted", {
          source: `${environment}userData`,
          id: data.id,
          email: data?.email ?? null,
          jwt: describeJwt(data?.jwt),
        });
        setUserInfo(data);
        setAccessJWT(data.jwt);
        getUserDetails(data.id, data.jwt);
        // store the JWT to local storage
        setItemInLocalStorage("_zenvoya_JWT", data.jwt);
        return;
      }
    }

    if (!userData) {
      embedAuth("anonymous", {
        reason: `no ${environment}userData cookie`,
        note: "session has no account until the portal posts SSO identity",
      });
      setUserInfo({
        id: "",
        name: "",
        email: "",
        phone: "",
        profile_pic: "",
        loggedIn: false,
        preferences: {},
        role: 0,
      });
      setItemInLocalStorage("_zenvoya_JWT", "");
    }
  }, []);

  const chatFallback = isMobile ? (
    <DelayedFallback>
      <ChatHomeSkeleton />
    </DelayedFallback>
  ) : null;

  return (
    <MobileScrollContainer>
      <Suspense fallback={SHOW_LOADING_DOTS ? <LoadingDots showText={false} /> : null}>
      <Routes>
        <Route path={"/app/code"} element={<GoogleOAuth />} />
        <Route path={MCP_CONTINUE_PATH} element={<McpContinue />} />

        <Route path={ROUTES.TripHelp} element={<TripHelp />} />
        <Route path={"/test"} element={<Test />} />
        <Route path={ROUTES.History} element={<HistoryPage />} />
        <Route path={ROUTES.BookingInProgress} element={<Suspense fallback={<DelayedFallback><BookingFormSkeleton type="stays" withHeader /></DelayedFallback>}><BookingInProgress /></Suspense>} />

        <Route path={ROUTES.BookingLoader} element={<BookingLoader />} />
        <Route path={ROUTES.ForYou} element={<RecommendedTrips />} />
        {/* Logged out, "/" is always sent to /onboarding (useAppNavigationLogic's auth
            redirect). Mounting the landing page for that one render downloaded the chat
            and trips code on every first visit, so show its skeleton instead. */}
        <Route
          path={ROUTES.Home}
          element={userInfo?.loggedIn ? <Suspense fallback={chatFallback}><DesktopLandingPage /></Suspense> : chatFallback}
        />

        <Route path={ROUTES.ItineraryDetails} element={<ItineraryDetails />} />
        <Route
          path={ROUTES.EditAccount}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={EditAccount} />}
        />
        <Route path={ROUTES.Account} element={<MobileAccount />} />
        <Route path={ROUTES.AboutUs} element={<AboutUs />} />
        <Route path={ROUTES.ResetPassword + "/:id"} element={<ResetPasswordPage />} />
        <Route path="/shared/:id" element={<ViewShareItinerary />} />
        {/* lares alert-resolution SMS deep link. Public (NOT ProtectedRoute) —
            gates auth in-component, mirroring /trip + /shared/:id. */}
        <Route path={ROUTES.TripById} element={<TripsById />} />
        <Route path={ROUTES.TopDestination} element={<TopDestination />} />
        <Route path={ROUTES.SavedItineraryDetails} element={<SavedItineraryDetail />} />

        <Route path={ROUTES.AdjustTrip} element={<AdjustTrip />} />
        <Route
          path={ROUTES.PreviewPreference}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={PreviewPreference} />}
        />
        <Route
          path={ROUTES.preferences}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={Preferences} />}
        />
        <Route
          path={ROUTES.ModifyFlights}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={ModifyFlights} />}
        />
        <Route path={ROUTES.ReorderMultiCityTrip} element={<ReorderMultiCityTrip />} />
        <Route path={ROUTES.Faqs} element={<FaqAccordion />} />
        <Route path={ROUTES.Tos} element={<TermsOfService />} />
        <Route path={ROUTES.PrivacyPolicy} element={<PrivacyPolicy />} />
        <Route path={ROUTES.PaymentSuccess} element={<StripeSuccess />} />
        <Route path={ROUTES.PaymentFailure} element={<PaymentFailure />} />
        <Route path={ROUTES.NewTrip} element={<Suspense fallback={chatFallback}><NewTrip /></Suspense>} />
        <Route
          path={ROUTES.ModifyHotelReservation}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={ModifyHotelReservation} />}
        />
        <Route
          path={ROUTES.HotelDetails}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={HotelDetails} />}
        />
        <Route path={ROUTES.Verify} element={<EmailVerificationMessage />} />
        <Route path={ROUTES.VerifyOld} element={<VerifyEmailRedirect />} />
        <Route
          path={ROUTES.TravelerInformation}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={TravelerInformation} />}
        />
        <Route path={"/today"} element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={TodayTrip} />} />
        <Route path={ROUTES.MyTrips} element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={MyTrips} />} />
        <Route
          path={ROUTES.SavedTrip}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={SavedTrip} />}
        />
        <Route
          path={ROUTES.weatherAdvisory}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={WeatherAdvisory} />}
        />
        <Route
          path={ROUTES.FlightDelay}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={FlightDelay} />}
        />
        <Route
          path={ROUTES.FlightSelect}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={FlightSelect} />}
        />
        <Route
          path={ROUTES.ExploreCity}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={ExploreTripAndAssistance} />}
        />
        <Route
          path={ROUTES.FaqAssistance}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={FaqAssistance} />}
        />
        <Route path={ROUTES.TripDetails} element={<TripDetailsPage />} />
        <Route path={ROUTES.TripDetailsOld} element={<TripDetailsRedirect />} />

        <Route path={ROUTES.RecommendedTripDetails} element={<RecommendedTripDetailsPage />} />
        <Route path={ROUTES.ModifyHotelsSuccess} element={<ModifyHotelsSuccess />} />
        <Route path={ROUTES.onBoarding} element={<OnBoarding />} />
        <Route path={ROUTES.TicketDetails + "/:ticketId"} element={<TicketDetails />} />
        <Route path={ROUTES.PortalChat} element={<PortalSurface surface="chat" />} />
        <Route path={ROUTES.PortalShop} element={<PortalSurface surface="shop" />} />
        <Route path={ROUTES.PortalHistory} element={<PortalSurface surface="history" />} />
        <Route path={ROUTES.PortalTrips} element={<PortalSurface surface="trips" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </MobileScrollContainer>
  );
}
